import {
  CashIcon,
  ForwardArrowIcon,
  SuccessIcon,
  WalletIcon,
} from "@src/assets";
import { TokenLogo, Spinner, SwapperSpinner } from "@src/components";
import { useSwapContext } from "@src/contexts";
import { SwapMode } from "@src/interfaces";
import { TokenWithChain } from "@src/models";
import { abbreviateNumber, shortAddress } from "@src/utils";
import { ethers } from "ethers";
import { useEffect, useMemo, useState } from "react";

interface Props {
  type: "simpleSwap" | "advancedSwap" | "transfer";
  cashAmount?: string;
  fromAmount: string;
  toAmount?: string;
  receiverAddress?: string;
  token?: TokenWithChain;
  displayToken?: TokenWithChain;
  mining?: boolean;
}

export const PendingTransactionView = ({
  type,
  cashAmount,
  fromAmount,
  toAmount,
  receiverAddress,
  token,
  displayToken,
  mining = false,
}: Props) => {
  const { allTokens, swapMode, chain } = useSwapContext();
  const [miningLongerThenExpected, setMiningLongerThenExpected] =
    useState(false);
  const nativeToken = useMemo(() => {
    return allTokens.find(
      (token_) =>
        token_.chainId === token?.chainId &&
        token_.address === ethers.constants.AddressZero,
    );
  }, [allTokens, token]);
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (mining) {
      timer = setTimeout(() => setMiningLongerThenExpected(true), 30000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [mining]);
  return (
    <div className="flex flex-col gap-2 h-full">
      {type === "transfer" && (
        <div className="flex flex-col bg-card_bg rounded-[12px] px-4 py-3 items-start gap-4">
          <div className="flex flex-col items-start gap-1">
            <div className="text-xs text-text_primary opacity-60">
              You are transfering
            </div>
            <div className="flex gap-1 items-center">
              <div className="font-bold text-xl text-text_primary leading-5">
                {abbreviateNumber(fromAmount)}
              </div>
              <div className="flex gap-1 items-end">
                <TokenLogo
                  token={token}
                  tokenLogoClassName="w-5 min-w-5 h-5"
                  unknownTokenLogoClassName="w-5 min-w-5 h-5 text-sm"
                  chain={chain}
                />
                <div className="text-xs font-medium text-text_primary leading-3">
                  {token?.symbol}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-start gap-1">
            <div className="text-xs text-text_primary opacity-60">to</div>
            <div className="flex gap-1 items-center">
              <WalletIcon />
              <div className="font-bold text-sm text-text_primary">
                {shortAddress(receiverAddress || "")}
              </div>
            </div>
          </div>
        </div>
      )}
      {type === "simpleSwap" && (
        <div className="flex flex-col bg-card_bg rounded-[12px] px-4 py-3 items-start gap-4">
          <div className="flex flex-col items-start gap-1">
            <div className="text-xs text-text_primary opacity-60">You pay</div>
            <div className="flex gap-1 items-center">
              <div className="font-bold text-xl text-text_primary leading-5">
                {abbreviateNumber(
                  fromAmount,
                  swapMode === SwapMode.Buy ? 2 : undefined,
                )}
              </div>
              {swapMode === SwapMode.Buy ? (
                <div className="flex gap-1 items-end">
                  <CashIcon className="w-5 h-5" />
                  <div className="text-xs text-text_primary font-medium">
                    Cash
                  </div>
                </div>
              ) : (
                <div className="flex gap-1 items-end">
                  <TokenLogo
                    token={token}
                    tokenLogoClassName="w-5 min-w-5 h-5"
                    unknownTokenLogoClassName="w-5 min-w-5 h-5 text-sm"
                    chain={chain}
                  />
                  <div className="text-xs font-medium text-text_primary leading-3">
                    {token?.symbol}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col items-start gap-1">
            <div className="text-xs text-text_primary opacity-60">You get</div>
            <div className="flex gap-1 items-center">
              <div className="font-bold text-xl text-button_text_secondary leading-5">
                {abbreviateNumber(toAmount || "0")}
              </div>
              {swapMode === SwapMode.Sell ? (
                <div className="flex gap-1 items-end">
                  <CashIcon className="w-5 h-5" />
                  <div className="text-xs text-text_primary font-medium">
                    Cash
                  </div>
                </div>
              ) : (
                <div className="flex gap-1 items-end">
                  <TokenLogo
                    token={displayToken || token}
                    tokenLogoClassName="w-5 min-w-5 h-5"
                    unknownTokenLogoClassName="w-5 min-w-5 h-5 text-sm"
                    chain={chain}
                  />
                  <div className="text-xs font-medium text-text_primary leading-3">
                    {displayToken?.symbol || token?.symbol}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {type === "advancedSwap" && (
        <div className="flex flex-col bg-card_bg rounded-[12px] px-4 py-3 items-start gap-4">
          <div className="flex gap-2 items-center w-full">
            <div className="flex flex-col items-start gap-1">
              <div className="text-xs text-text_primary opacity-60">
                You deposit
              </div>
              <div className="flex gap-1 items-center">
                <div className="font-bold text-xl text-text_primary leading-5">
                  {abbreviateNumber(cashAmount || "0", 2)}
                </div>
              </div>
            </div>
            <ForwardArrowIcon />
            <div className="flex flex-col items-start gap-1">
              <div className="text-xs text-text_primary opacity-60">
                You receive
              </div>
              <div className="flex gap-1 items-center">
                <div className="font-bold text-xl text-text_primary leading-5">
                  {abbreviateNumber(fromAmount)}
                </div>
                <div className="flex gap-1 items-end">
                  <TokenLogo
                    token={nativeToken!}
                    tokenLogoClassName="w-5 min-w-5 h-5"
                    unknownTokenLogoClassName="w-5 min-w-5 h-5 text-sm"
                    chain={chain}
                  />
                  <div className="text-xs font-medium text-text_primary leading-3">
                    {nativeToken?.symbol}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-1" />
            <SuccessIcon className="w-5 h-5" />
          </div>

          <div className="flex gap-2 items-center w-full">
            <div className="flex flex-col items-start gap-1">
              <div className="text-xs text-text_primary opacity-60">
                We swap
              </div>
              <div className="flex gap-1 items-center">
                <div className="font-bold text-xl text-text_primary leading-5">
                  {abbreviateNumber(fromAmount)}
                </div>
                <div className="flex gap-1 items-end">
                  <TokenLogo
                    token={nativeToken!}
                    tokenLogoClassName="w-5 min-w-5 h-5"
                    unknownTokenLogoClassName="w-5 min-w-5 h-5 text-sm"
                    chain={chain}
                  />
                  <div className="text-xs font-medium text-text_primary leading-3">
                    {nativeToken?.symbol}
                  </div>
                </div>
              </div>
            </div>
            <ForwardArrowIcon />
            <div className="flex flex-col items-start gap-1">
              <div className="text-xs text-text_primary opacity-60">
                You get
              </div>
              <div className="flex gap-1 items-center">
                <div className="font-bold text-xl text-text_primary leading-5">
                  {abbreviateNumber(toAmount || "0")}
                </div>
                <div className="flex gap-1 items-end">
                  <TokenLogo
                    token={token}
                    tokenLogoClassName="w-5 min-w-5 h-5"
                    unknownTokenLogoClassName="w-5 min-w-5 h-5 text-sm"
                    chain={chain}
                  />
                  <div className="text-xs font-medium text-text_primary leading-3">
                    {token?.symbol}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-1" />
            <Spinner className="w-5 h-5" />
          </div>
        </div>
      )}
      <div className="flex flex-col flex-1 bg-card_bg rounded-[12px] px-4 py-3 justify-center items-center">
        <div className="flex flex-col w-full h-[180px] items-center gap-5">
          <SwapperSpinner className="!w-[64px]" />
          <div className="flex flex-col gap-2 items-center justify-center w-[80%] text-center">
            <div className="text-xs text-text_primary">
              {`Please wait while we ${
                type === "transfer" ? "transfer" : "swap"
              } the tokens for you`}
            </div>
            <div
              className={`text-xs text-text_primary transition-opacity duration-300 ${
                mining ? "opacity-100" : "opacity-0"
              }`}
            >
              {`Your transaction is in progress...`}
            </div>

            <div
              className={`text-xs text-text_primary transition-opacity duration-300 ${
                miningLongerThenExpected ? "opacity-100" : "opacity-0"
              }`}
            >
              {`Your transaction is taking longer than usual due to network congestion. Thanks for your patience!`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
